import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { Save, RotateCcw, AlertCircle } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { useToast } from '../../../ui/Toast';
import {
  useTherapistAvailability,
  useUpdateAvailability,
} from '../../../../hooks/useTherapistDashboard';
import type { AvailabilitySlot } from '../../../../types/therapist-dashboard.types';
import { DAYS_OF_WEEK, createDefaultSlot } from './constants';
import { DaySlotEditor } from './DaySlotEditor';
import { WeeklySummary } from './WeeklySummary';
import { AvailabilityLoadingState } from './AvailabilityLoadingState';
import { AvailabilityErrorState } from './AvailabilityErrorState';
import { AvailabilityInfoCard } from './AvailabilityInfoCard';

export const AvailabilitySection: FC = () => {
  const { showToast } = useToast();
  const { data: serverSlots, isLoading, error, refetch } = useTherapistAvailability();
  const updateMutation = useUpdateAvailability();

  // Local state for editing
  const [localSlots, setLocalSlots] = useState<AvailabilitySlot[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local slots from server data
  useEffect(() => {
    if (serverSlots) {
      // Create a complete week with server data merged in
      const therapistId = serverSlots[0]?.therapistId || 'current';
      const completeWeek = DAYS_OF_WEEK.map((day) => {
        const existingSlot = serverSlots.find((s) => s.dayOfWeek === day.value);
        return existingSlot || createDefaultSlot(therapistId, day.value);
      });
      setLocalSlots(completeWeek);
      setHasChanges(false);
    }
  }, [serverSlots]);

  // Check if there are unsaved changes
  const checkForChanges = useCallback(
    (slots: AvailabilitySlot[]) => {
      if (!serverSlots) return false;

      return slots.some((slot) => {
        const serverSlot = serverSlots.find((s) => s.dayOfWeek === slot.dayOfWeek);
        if (!serverSlot) {
          // New slot with availability is a change
          return slot.isAvailable;
        }
        return (
          slot.isAvailable !== serverSlot.isAvailable ||
          slot.startTime !== serverSlot.startTime ||
          slot.endTime !== serverSlot.endTime
        );
      });
    },
    [serverSlots]
  );

  // Toggle day availability
  const handleToggle = useCallback(
    (dayOfWeek: number) => {
      setLocalSlots((prev) => {
        const updated = prev.map((slot) =>
          slot.dayOfWeek === dayOfWeek ? { ...slot, isAvailable: !slot.isAvailable } : slot
        );
        setHasChanges(checkForChanges(updated));
        return updated;
      });
    },
    [checkForChanges]
  );

  // Update time for a day
  const handleTimeChange = useCallback(
    (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => {
      setLocalSlots((prev) => {
        const updated = prev.map((slot) =>
          slot.dayOfWeek === dayOfWeek ? { ...slot, [field]: value } : slot
        );
        setHasChanges(checkForChanges(updated));
        return updated;
      });
    },
    [checkForChanges]
  );

  // Reset to server state
  const handleReset = useCallback(() => {
    if (serverSlots) {
      const therapistId = serverSlots[0]?.therapistId || 'current';
      const completeWeek = DAYS_OF_WEEK.map((day) => {
        const existingSlot = serverSlots.find((s) => s.dayOfWeek === day.value);
        return existingSlot || createDefaultSlot(therapistId, day.value);
      });
      setLocalSlots(completeWeek);
      setHasChanges(false);
    }
  }, [serverSlots]);

  // Validation
  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    localSlots.forEach((slot) => {
      if (slot.isAvailable && slot.startTime >= slot.endTime) {
        const dayName = DAYS_OF_WEEK.find((d) => d.value === slot.dayOfWeek)?.label;
        errors.push(`${dayName}: End time must be after start time`);
      }
    });
    return errors;
  }, [localSlots]);

  // Save changes
  const handleSave = useCallback(async () => {
    if (validationErrors.length > 0) {
      showToast({
        type: 'error',
        title: 'Validation Error',
        message: validationErrors[0] ?? 'Please fix the errors before saving.',
      });
      return;
    }

    // Only send slots that are available
    const slotsToSave = localSlots.filter((slot) => slot.isAvailable);

    updateMutation.mutate(slotsToSave, {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: 'Availability Updated',
          message: 'Your availability has been saved successfully.',
        });
        setHasChanges(false);
        refetch();
      },
      onError: (err) => {
        showToast({
          type: 'error',
          title: 'Update Failed',
          message: err.message || 'Failed to save availability. Please try again.',
        });
      },
    });
  }, [localSlots, validationErrors, updateMutation, showToast, refetch]);

  // Loading state
  if (isLoading) {
    return <AvailabilityLoadingState />;
  }

  // Error state
  if (error) {
    return <AvailabilityErrorState message={error.message} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Availability</h2>
          <p className="text-gray-600 dark:text-slate-400 mt-1">
            Set your weekly working hours for client bookings
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleReset}
            disabled={!hasChanges || updateMutation.isPending}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
              'border border-gray-300 dark:border-slate-600',
              'text-gray-700 dark:text-slate-300',
              'hover:bg-gray-50 dark:hover:bg-slate-700',
              'transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <RotateCcw className="w-4 h-4" />
            <span className="hidden sm:inline">Reset</span>
          </button>

          <button
            onClick={handleSave}
            disabled={!hasChanges || validationErrors.length > 0 || updateMutation.isPending}
            className={cn(
              'flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-medium',
              'bg-blue-600 text-white',
              'hover:bg-blue-700',
              'transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {updateMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Unsaved Changes Banner */}
      {hasChanges && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            You have unsaved changes. Click "Save Changes" to update your availability.
          </p>
        </div>
      )}

      {/* Info Card */}
      <AvailabilityInfoCard />

      {/* Weekly Schedule Editor */}
      <div className="space-y-3">
        {DAYS_OF_WEEK.map((day) => {
          const slot = localSlots.find((s) => s.dayOfWeek === day.value);
          if (!slot) return null;

          return (
            <DaySlotEditor
              key={day.value}
              slot={slot}
              dayConfig={day}
              onToggle={handleToggle}
              onTimeChange={handleTimeChange}
            />
          );
        })}
      </div>

      {/* Summary */}
      <WeeklySummary slots={localSlots} />
    </div>
  );
};
